import {
  Component,
  HostListener,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";

import swal from "sweetalert2";
import { NOTAS_EXCEL } from "../notas-excel";
import { LIMITES } from "../limites";
import { avisarPropuesta, quedoComoPropuesta } from "../propuesta";
import { ModalMatrizRiesgosIAComponent } from "../modal-matriz-riesgos-ia/modal-matriz-riesgos-ia.component";
import { ExpedienteActivoIAComponent } from "../expediente-activo-ia/expediente-activo-ia.component";
import { FechaBitacoraPipe } from "../fecha-bitacora.pipe";
import { ordenReciente } from "../cortes";
import { MESES_ACTIVOS, ProximoMonitoreo, proximoMonitoreo } from "../monitoreo";
import { HojaMatriz, HojaMatrizBase } from "../matriz-comun/hoja-matriz-base";
import { deDimension } from "../matriz-comun/hoja-criterios";
import {
  CatProbabilidad,
  CatValoracion,
  MatrizAccion,
  MatrizActivo,
  MatrizAmenaza,
  MatrizEvidencia,
  MatrizHistorial,
  MatrizSeguimiento,
  MatrizVulnerabilidad,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";
import { AlertaMonitoreoService } from "../alerta-monitoreo/alerta-monitoreo.service";

/**
 * Una fila plana de la matriz, equivalente a un renglón de la hoja "Activos".
 * Los rowspan reproducen las celdas combinadas del Excel: los datos del activo
 * ocupan todas las filas de su bloque, los de la amenaza todas las de sus
 * vulnerabilidades, y así.
 *
 * Sólo se usa en la vista previa del formato; las tarjetas leen el árbol tal
 * como viene.
 */
interface Fila {
  activo: MatrizActivo;
  amenaza: MatrizAmenaza | null;
  vulnerabilidad: MatrizVulnerabilidad | null;
  accion: MatrizAccion | null;
  primeraDelActivo: boolean;
  spanActivo: number;
  primeraDeAmenaza: boolean;
  spanAmenaza: number;
  primeraDeVulnerabilidad: boolean;
  spanVulnerabilidad: number;
  /**
   * El corte que reporta ESTA acción, para las dos últimas columnas del
   * formato ("Seguimiento del Riesgo"). Se amarra por idMatrizAccion, no
   * por posición: el backend devuelve los cortes de más nuevo a más viejo
   * y las acciones de la primera a la última, así que emparejarlos por
   * índice pondría cada resultado en la fila equivocada.
   */
  corte: MatrizSeguimiento | null;
}

/**
 * Hoja "Activos" en tarjetas: una por activo, con su valoración, el riesgo
 * antes y después del plan, el árbol de amenazas desplegable y sus cortes de
 * monitoreo.
 *
 * La tabla de 27 columnas del formato no desaparece: queda como vista previa
 * de solo lectura, para quien necesita ver la hoja como la imprime el Excel.
 *
 * La captura no se hace aquí. Editar y agregar abren el editor anidado
 * (ModalMatrizRiesgosIA), que es el que sabe manejar activo -> amenaza ->
 * vulnerabilidad -> acción. Importa que sea así: el backend reemplaza el árbol
 * completo en cada guardado, de modo que mandar un activo sin sus amenazas lo
 * dejaría sin ellas sin avisar.
 */
@Component({
  selector: "app-matriz-completa-ia",
  standalone: true,
  templateUrl: "./matriz-completa-ia.component.html",
  styleUrls: ["./matriz-completa-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatTooltipModule,
    ModalMatrizRiesgosIAComponent,
    ExpedienteActivoIAComponent,
    FechaBitacoraPipe,
  ],
})
export class MatrizCompletaIAComponent extends HojaMatrizBase<MatrizActivo, MatrizHistorial> {
  private readonly alertaMonitoreo = inject(AlertaMonitoreoService);

  /**
   * Lo que la papelera, las copias y las evidencias dicen de un activo. La de
   * IA es hoy la única matriz que trabaja con activos.
   */
  protected readonly hoja: HojaMatriz = {
    entidad: "ACTIVO",
    origenEvidencia: "ACTIVO",
    esteRegistro: "este activo",
    vuelveDePapelera: "Vuelve con su folio, sus amenazas, sus cortes y su bitácora, tal como estaba ",
    volvioDePapelera: "El activo volvió a la matriz.",
    errorPapelera: "No se pudo restaurar el activo.",
    volveraACopia: "El activo volverá",
    reemplazaConCopia: "Sus amenazas, vulnerabilidades y acciones se reemplazan por las de la copia. ",
    volvioACopia: "El activo volvió a la copia.",
  };

  /**
   * Límites de captura, tomados del largo real de cada columna. Van al
   * maxlength de los campos: producción corre en modo estricto y un texto más
   * largo que su columna aborta el guardado en vez de truncarse.
   */
  lim = LIMITES;

  activos: MatrizActivo[] = [];
  filas: Fila[] = [];

  valoraciones: CatValoracion[] = [];
  probabilidades: CatProbabilidad[] = [];

  cargando = true;

  /** Filtro por clasificación del riesgo actual. */
  filtro = "Todas";
  readonly bandas = ["Bajo", "Medio", "Alto", "Muy Alto"];

  /**
   * Búsqueda por texto. Va del lado del navegador y no como filtro del
   * backend: los activos ya están cargados con su árbol, y consultar de nuevo
   * costaría una petición por activo.
   */
  busqueda = "";

  /** Notas del Excel: salen al pasar el mouse en la vista previa. */
  notas = NOTAS_EXCEL;

  /** Árboles de amenazas abiertos, por id de activo. */
  abiertos = new Set<number>();

  /** Cortes de monitoreo abiertos, por id de activo. */
  cortesAbiertos = new Set<number>();
  nuevoCorte: { [id: number]: MatrizSeguimiento } = {};
  guardandoCorte = new Set<number>();

  /**
   * Modo lectura. Lo decide el backend por perfil (sólo cinco personas
   * editan la matriz) y aquí sólo esconde botones: el candado de verdad está
   * en el servidor, que lo vuelve a comprobar en cada escritura.
   */
  puedeEditar = false;

  /**
   * El backend guarda el nombre físico de la columna. Si se agrega un campo al
   * diff de MatrizRiesgoBuss, hay que agregarlo también aquí.
   */
  private static readonly ETIQUETAS: { [k: string]: string } = {
    nombre_activo: "Nombre del activo",
    tipo_activo: "Tipo de activo",
    val_confidencialidad: "Confidencialidad",
    val_integridad: "Integridad",
    val_disponibilidad: "Disponibilidad",
    justificacion_valoracion: "Justificación de la valoración",
    activos_dependientes: "Activos dependientes",
    id_inventario_ia: "Folio del inventario IA",
    fecha_identificacion: "Fecha de identificación",
    amenazas: "Amenazas",
    seguimiento: "Seguimiento",
    estatus: "Estatus",
    // El renglón con que nace el activo, igual que en el plan de gestión.
    alta: "Alta del activo",
    // Lo escribió el script que trajo la matriz desde el Excel. Ya no se
    // genera: la bitácora sembrada quedó como 'alta'. Se conserva por si
    // alguna base todavía no ha corrido la limpieza.
    migracion: "Origen del registro",
  };

  /** Editor anidado: id del activo, o 0 para dar de alta uno nuevo. */
  idEditando: number | null = null;

  /**
   * Expediente de trazabilidad abierto (o null). La ventana se cuelga sola de
   * <body>, así que aquí basta con prenderla y apagarla.
   */
  expediente: MatrizActivo | null = null;

  /* ---------- Pantalla completa (vista previa y editor) ---------- */

  @HostListener("document:keydown.escape")
  escape(): void {
    // La comparación va primero: es lo que está al frente y es de sólo
    // lectura, así que cerrarla no se lleva nada a medio escribir.
    if (this.comparaActivo) {
      this.cerrarComparacion();
      this.cdr.detectChanges();
      return;
    }
    // Con el editor abierto no hace nada a propósito: es un formulario y no se
    // descarta con una tecla suelta.
    if (this.idEditando === null && this.vistaPrevia) {
      this.vistaPrevia = false;
      this.sincronizarPantalla();
    }
  }

  /* ---------- Carga ---------- */

  cargar(): void {
    this.cargando = true;
    this.cdr.detectChanges();
    this.service.catalogos().subscribe({
      next: (c) => {
        this.valoraciones = (c && c.valoraciones) || [];
        this.probabilidades = (c && c.probabilidades) || [];
      },
      error: (e) => console.error("Error al cargar catálogos", e),
    });

    // Una sola llamada: trae los activos con su árbol y sus cortes. Antes se
    // pedía el detalle activo por activo y eran N peticiones.
    this.service.consultarCompleto({ idMatriz: this.idMatriz }).subscribe({
      next: (res) => {
        // Sin sesión —o ante un error de negocio— el backend responde 200 con
        // el cuerpo vacío: sin esta guarda, la pantalla se quedaba en
        // "Cargando…" para siempre.
        if (!res) {
          this.cargando = false;
          this.cdr.detectChanges();
          swal.fire("No se pudo cargar", "Vuelve a intentarlo o recarga la página.", "warning");
          return;
        }
        this.puedeEditar = res.puedeEditar === true;
        const lista = res.activos || [];
        // El backend devuelve el resumen —sin árbol— si no pudo cargar el
        // detalle de alguno. Se distingue porque amenazas viene sin definir,
        // no como lista vacía.
        this.finCarga(lista, lista.filter((a) => !a.amenazas).length);
      },
      error: (e) => {
        console.error("Error al consultar la matriz", e);
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  /** El número del folio: de ACT-002 saca 2. Sirve igual con MR-0002. */
  private numeroFolio(a: MatrizActivo): number {
    const partes = String(a.consecutivo || "").split("-");
    const n = Number(partes[partes.length - 1]);
    // Sin folio legible se va al final, no al principio.
    return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
  }

  private finCarga(lista: MatrizActivo[], fallidos = 0): void {
    // Por FOLIO: ACT-001, ACT-002, ACT-003... Antes ordenaba por el id
    // interno, que es el orden en que se capturaron, y la lista salía
    // descolocada respecto de los folios y del Inventario de IA.
    lista.sort((a, b) => this.numeroFolio(a) - this.numeroFolio(b)
      || (a.idMatrizActivo || 0) - (b.idMatrizActivo || 0));
    this.activos = lista;
    // Se conserva lo que el usuario tenga a medio capturar en otras tarjetas.
    const anteriores = this.nuevoCorte;
    this.nuevoCorte = {};
    lista.forEach((a) => {
      if (a.idMatrizActivo) {
        this.nuevoCorte[a.idMatrizActivo] =
          anteriores[a.idMatrizActivo] || { fechaMonitoreo: "", resultado: "" };
      }
    });
    this.construirFilas();
    this.cargando = false;
    this.cdr.detectChanges();
    if (fallidos > 0) {
      swal.fire(
        "Carga incompleta",
        "No se pudo traer el detalle de " + fallidos + " activo(s); " +
          "se muestran sin sus amenazas ni sus cortes. Recarga para reintentar.",
        "warning"
      );
    }
  }

  /**
   * El corte que reporta esta acción. Devuelve el más reciente si hubiera
   * más de uno, y null si la acción todavía no tiene seguimiento.
   */
  private corteDeAccion(a: MatrizActivo, ac: MatrizAccion | null): MatrizSeguimiento | null {
    if (!ac || !ac.idMatrizAccion || !a.seguimientos || !a.seguimientos.length) {
      return null;
    }
    const suyos = a.seguimientos.filter((s) => s.idMatrizAccion === ac.idMatrizAccion);
    if (!suyos.length) {
      return null;
    }
    // Arranca con el primero: compararlo consigo mismo no lo cambia (es «>»).
    return suyos.reduce((masNuevo, s) =>
      this.aTiempo(s.fechaMonitoreo) > this.aTiempo(masNuevo.fechaMonitoreo) ? s : masNuevo, suyos[0]);
  }

  /**
   * La fecha de monitoreo llega como texto o como Date segun por donde
   * entre, asi que para ordenar hay que pasarla a numero.
   */
  private aTiempo(f: string | Date | null | undefined): number {
    if (!f) {
      return 0;
    }
    const t = new Date(f).getTime();
    return Number.isNaN(t) ? 0 : t;
  }

  /**
   * La fecha que va en la columna combinada "Última Fecha de Monitoreo":
   * la del corte más reciente del activo, como en el formato.
   */
  ultimaFechaMonitoreo(a: MatrizActivo): string | Date | null {
    if (!a.seguimientos || !a.seguimientos.length) {
      return null;
    }
    let mejor: MatrizSeguimiento | null = null;
    for (const s of a.seguimientos) {
      if (!s.fechaMonitoreo) {
        continue;
      }
      if (!mejor || this.aTiempo(s.fechaMonitoreo) > this.aTiempo(mejor.fechaMonitoreo)) {
        mejor = s;
      }
    }
    return mejor ? mejor.fechaMonitoreo || null : null;
  }

  /** Aplana el árbol para la vista previa, con los rowspan del formato. */
  private construirFilas(): void {
    const filas: Fila[] = [];
    for (const a of this.activos) {
      const amenazas = a.amenazas && a.amenazas.length ? a.amenazas : [null];
      const inicioActivo = filas.length;
      for (const am of amenazas) {
        const vulns =
          am && am.vulnerabilidades && am.vulnerabilidades.length
            ? am.vulnerabilidades
            : [null];
        const inicioAmenaza = filas.length;
        for (const vu of vulns) {
          const acciones = vu && vu.acciones && vu.acciones.length ? vu.acciones : [null];
          const inicioVuln = filas.length;
          for (const ac of acciones) {
            filas.push({
              activo: a,
              amenaza: am,
              vulnerabilidad: vu,
              accion: ac,
              primeraDelActivo: false,
              spanActivo: 0,
              primeraDeAmenaza: false,
              spanAmenaza: 0,
              primeraDeVulnerabilidad: false,
              spanVulnerabilidad: 0,
              corte: this.corteDeAccion(a, ac),
            });
          }
          filas[inicioVuln].primeraDeVulnerabilidad = true;
          filas[inicioVuln].spanVulnerabilidad = filas.length - inicioVuln;
        }
        filas[inicioAmenaza].primeraDeAmenaza = true;
        filas[inicioAmenaza].spanAmenaza = filas.length - inicioAmenaza;
      }
      filas[inicioActivo].primeraDelActivo = true;
      filas[inicioActivo].spanActivo = filas.length - inicioActivo;
    }
    this.filas = filas;
  }

  /* ---------- Filtro ---------- */

  get filtrados(): MatrizActivo[] {
    let lista = this.activos;
    if (this.filtro !== "Todas") {
      lista = lista.filter((a) => a.clasificacionActual === this.filtro);
    }
    const q = this.normalizar(this.busqueda);
    if (!q) {
      return lista;
    }
    // Busca en folio, nombre, folio del inventario, tipo y la solicitud de
    // la que vino (SIA-0044): es lo que alguien tiene en la cabeza cuando
    // busca un activo.
    return lista.filter((a) =>
      this.normalizar(
        (a.consecutivo || "") + " " + (a.nombreActivo || "") + " " +
        (a.inventarioConsecutivo || "") + " " + (a.tipoActivo || "") + " " +
        (a.solicitudFolio || "")
      ).indexOf(q) >= 0
    );
  }

  /** Sin acentos ni mayúsculas: "codex" encuentra "Códex". */
  private normalizar(t: string): string {
    return (t || "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim();
  }

  limpiarBusqueda(): void {
    this.busqueda = "";
  }

  conteo(banda: string): number {
    return this.activos.filter((a) => a.clasificacionActual === banda).length;
  }

  /* ---------- Expediente de trazabilidad ---------- */

  abrirExpediente(a: MatrizActivo): void {
    this.expediente = a;
  }

  cerrarExpediente(): void {
    this.expediente = null;
  }

  /* ---------- Evidencias adjuntas del activo ----------
   * Imagen, PDF, Excel... del tratamiento del riesgo. Se piden solo al
   * desplegar la sección, igual que la bitácora. */

  subirEvidencia(a: MatrizActivo, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    // Se limpia para poder volver a elegir el mismo archivo después.
    input.value = "";
    if (!archivo || !a.idMatrizActivo) {
      return;
    }
    const id = a.idMatrizActivo;
    this.subiendoEvidencia.add(id);
    this.vigilaSubida(id, this.service.subirEvidencia(archivo, "ACTIVO", id));
  }

  eliminarEvidencia(a: MatrizActivo, evd: MatrizEvidencia): void {
    swal.fire({
      title: "¿Eliminar la evidencia?",
      text: (evd.nombreArchivo || "") + " — se borra el archivo del servidor y no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((r) => {
      if (!r.isConfirmed || !evd.idEvidencia || !a.idMatrizActivo) {
        return;
      }
      this.service.eliminarEvidencia(evd.idEvidencia).subscribe((res) => {
        if (res && res.codigoError) {
          swal.fire("No se pudo eliminar", res.mensajeError || "", "error");
        }
        this.cargarEvidencias(a.idMatrizActivo!);
      });
    });
  }

  /**
   * "No aplica": la sección se esconde y queda una línea discreta con el
   * botón para revertir. El backend rechaza NO si ya hay archivos.
   */
  marcarNoAplicaEvidencias(a: MatrizActivo): void {
    swal.fire({
      title: "¿Marcar evidencias como No aplica?",
      text: "La sección se ocultará para " + (a.nombreActivo || "este activo") +
        " y el expediente lo reportará como estado justificado. Se puede revertir cuando quieras.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, no aplica",
      cancelButtonText: "Cancelar",
    }).then((r) => {
      if (r.isConfirmed && a.idMatrizActivo) {
        this.guardarAplicaEvidencias(a.idMatrizActivo, "NO");
      }
    });
  }

  marcarSiAplicaEvidencias(a: MatrizActivo): void {
    if (a.idMatrizActivo) {
      this.guardarAplicaEvidencias(a.idMatrizActivo, "SI");
    }
  }

  /* ---------- Presentación ---------- */

  claseRiesgo(clasificacion?: string): string {
    switch (clasificacion) {
      case "Muy Alto": return "r-muyalto";
      case "Alto": return "r-alto";
      case "Medio": return "r-medio";
      case "Bajo": return "r-bajo";
      default: return "";
    }
  }

  etiquetaProb(aplicaA: string, nivel?: number): string {
    if (nivel === null || nivel === undefined) {
      return "";
    }
    const p = this.probabilidades.find((x) => x.aplicaA === aplicaA && x.nivel === nivel);
    return p && p.etiqueta ? p.etiqueta : String(nivel);
  }

  /** Los cinco niveles de una dimensión, para el select de la tarjeta. */
  valoracionPor(dimension: string): CatValoracion[] {
    return deDimension(this.valoraciones, dimension);
  }

  etiquetaValoracion(dimension: string, nivel?: number): string {
    if (nivel === null || nivel === undefined) {
      return "";
    }
    const v = this.valoraciones.find((x) => x.dimension === dimension && x.nivel === nivel);
    return v && v.etiqueta ? v.etiqueta : String(nivel);
  }

  /** Cuánto baja el riesgo con el plan. Negativo = sube. */
  mejora(a: MatrizActivo): number | null {
    if (a.riesgoActual == null || a.riesgoResidual == null) {
      return null;
    }
    return a.riesgoActual - a.riesgoResidual;
  }

  /** Cuenta de amenazas, vulnerabilidades y acciones del activo. */
  cuenta(a: MatrizActivo): { amenazas: number; vulnerabilidades: number; acciones: number } {
    let vulnerabilidades = 0;
    let acciones = 0;
    (a.amenazas || []).forEach((am) => {
      (am.vulnerabilidades || []).forEach((vu) => {
        vulnerabilidades++;
        acciones += (vu.acciones || []).length;
      });
    });
    return { amenazas: (a.amenazas || []).length, vulnerabilidades, acciones };
  }

  alternarArbol(a: MatrizActivo): void {
    const id = a.idMatrizActivo!;
    if (this.abiertos.has(id)) {
      this.abiertos.delete(id);
    } else {
      this.abiertos.add(id);
    }
  }

  /* ---------- Monitoreo ---------- */

  /**
   * La línea del monitoreo, del corte más reciente al más antiguo
   * (29/09/2026): el primero es el último monitoreo y va marcado.
   */
  cortesRecientes(a: MatrizActivo): MatrizSeguimiento[] {
    return ordenReciente(a.seguimientos, (s) => s.idMatrizSeguimiento);
  }

  /** Cada cuántos meses se monitorea un activo, para el texto de ayuda. */
  readonly mesesActivos = MESES_ACTIVOS;

  /**
   * Cuándo le toca el siguiente monitoreo al activo (29/09/2026): cada 4
   * meses desde su último corte. Avisa desde 5 días antes (monitoreo.ts).
   */
  proximo(a: MatrizActivo): ProximoMonitoreo {
    const ultimo = this.cortesRecientes(a)[0];
    return proximoMonitoreo(null, ultimo && ultimo.fechaMonitoreo, a.fechaIdentificacion, true);
  }

  esPendiente(resultado?: string): boolean {
    return !!resultado && resultado.indexOf("NA - el resultado de este corte") === 0;
  }

  pendientes(a: MatrizActivo): number {
    if (!a.seguimientos) {
      return 0;
    }
    return a.seguimientos.filter((s) => this.esPendiente(s.resultado)).length;
  }

  alternarCortes(a: MatrizActivo): void {
    const id = a.idMatrizActivo!;
    if (this.cortesAbiertos.has(id)) {
      this.cortesAbiertos.delete(id);
    } else {
      this.cortesAbiertos.add(id);
    }
  }

  /* ---------- Corregir un corte ---------- */

  /** Copia editable del corte que se está corrigiendo, por id. */
  corteEditando: { [id: number]: MatrizSeguimiento } = {};
  guardandoEdicionCorte = new Set<number>();

  editandoCorte(s: MatrizSeguimiento): boolean {
    return !!(s.idMatrizSeguimiento && this.corteEditando[s.idMatrizSeguimiento]);
  }

  borradorCorte(s: MatrizSeguimiento): MatrizSeguimiento {
    return this.corteEditando[s.idMatrizSeguimiento!];
  }

  editarCorte(a: MatrizActivo, s: MatrizSeguimiento): void {
    // Sobre una copia: si se cancela, el corte no queda tocado.
    this.corteEditando[s.idMatrizSeguimiento!] = {
      idMatrizSeguimiento: s.idMatrizSeguimiento,
      idMatrizActivo: s.idMatrizActivo,
      idMatrizAccion: s.idMatrizAccion,
      fechaMonitoreo: this.aFecha(s.fechaMonitoreo),
      resultado: s.resultado,
    };
  }

  cancelarEdicionCorte(s: MatrizSeguimiento): void {
    delete this.corteEditando[s.idMatrizSeguimiento!];
  }

  guardarEdicionCorte(a: MatrizActivo, s: MatrizSeguimiento): void {
    const id = s.idMatrizSeguimiento!;
    const b = this.corteEditando[id];
    if (!b) {
      return;
    }
    if (!b.resultado || !b.resultado.trim()) {
      swal.fire(
        "Falta el resultado",
        "El resultado es obligatorio. Si no se realizó la revisión, escribe NA y la razón.",
        "warning"
      );
      return;
    }
    // La fecha no se edita: se manda siempre la del registro original.
    b.fechaMonitoreo = this.aFecha(s.fechaMonitoreo);
    this.guardandoEdicionCorte.add(id);
    this.cdr.detectChanges();
    this.service.actualizarSeguimiento(b).subscribe({
      next: (d) => {
        this.guardandoEdicionCorte.delete(id);
        if (!d || d.codigoError) {
          swal.fire("No se guardó", (d && d.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        delete this.corteEditando[id];
        // Con el control de cambios encendido no se corrigió: quedó
        // propuesto. Se cierra el borrador igual y se relee, que sigue como
        // estaba; el aviso dice dónde quedó.
        if (quedoComoPropuesta(d)) {
          void avisarPropuesta("La corrección del corte de " + (a.consecutivo || "este activo"));
        }
        // Corregir un corte deja copia: la lista de versiones se relee.
        this.refrescarListas(a.idMatrizActivo!);
        this.refrescar(a.idMatrizActivo!);
      },
      error: () => {
        this.guardandoEdicionCorte.delete(id);
        swal.fire("Error", "No se pudo guardar el corte.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Desde cuando se puede fechar un corte NUEVO: el mas tarde entre hoy y el
   * ultimo corte registrado. Ni hacia atras en la linea de tiempo, ni con
   * fecha pasada — un corte dice lo que se encontro, y se captura al hacerlo.
   */
  minNuevoCorte(a: MatrizActivo): string {
    const ultima = this.ultimaFechaCorte(a);
    const hoy = this.hoy();
    return ultima > hoy ? ultima : hoy;
  }

  /** Fecha del ultimo corte, para que la linea de tiempo no camine hacia atras. */
  ultimaFechaCorte(a: MatrizActivo): string {
    let max = "";
    (a.seguimientos || []).forEach((s) => {
      const f = this.aFecha(s.fechaMonitoreo);
      if (f > max) {
        max = f;
      }
    });
    return max;
  }

  /** Hoy en el formato del <input type="date">, con la fecha local. */
  private hoy(): string {
    const d = new Date();
    return d.getFullYear() + "-" +
           ("0" + (d.getMonth() + 1)).slice(-2) + "-" +
           ("0" + d.getDate()).slice(-2);
  }

  /** "2026-08-03" -> "03/08/2026", sin pasar por Date para no correr el dia. */
  private aDia(f: string): string {
    const p = (f || "").split("-");
    return p.length === 3 ? p[2] + "/" + p[1] + "/" + p[0] : f;
  }

  agregarCorte(a: MatrizActivo): void {
    const id = a.idMatrizActivo!;
    const nuevo = this.nuevoCorte[id];
    if (!nuevo || !nuevo.fechaMonitoreo) {
      swal.fire("Falta la fecha", "Indica la fecha del monitoreo.", "warning");
      return;
    }
    if (!nuevo.resultado || !nuevo.resultado.trim()) {
      swal.fire(
        "Falta el resultado",
        "El resultado es obligatorio. Si no se realizó la revisión, escribe NA y la razón.",
        "warning"
      );
      return;
    }
    const minimo = this.minNuevoCorte(a);
    if (nuevo.fechaMonitoreo! < minimo) {
      swal.fire(
        "Fecha no permitida",
        "El corte no puede quedar antes del " + this.aDia(minimo) +
          ": ni antes de hoy, ni antes del último corte registrado.",
        "warning"
      );
      return;
    }
    nuevo.idMatrizActivo = id;
    this.guardandoCorte.add(id);
    this.cdr.detectChanges();
    this.service.guardarSeguimiento(nuevo).subscribe({
      next: (res) => {
        this.guardandoCorte.delete(id);
        if (!res || res.codigoError) {
          swal.fire("No se guardó", (res && res.mensajeError) || "Error desconocido.", "warning");
          this.cdr.detectChanges();
          return;
        }
        if (quedoComoPropuesta(res)) {
          void avisarPropuesta("El corte nuevo de " + (a.consecutivo || "este activo"));
        }
        this.nuevoCorte[id] = { fechaMonitoreo: "", resultado: "" };
        this.refrescarListas(id);
        // Solo este activo, no la matriz entera: recargarla dispara una
        // petición por activo y hace parpadear toda la lista por un corte.
        this.refrescar(id);
        this.alertaMonitoreo.refrescar();
      },
      error: () => {
        this.guardandoCorte.delete(id);
        swal.fire("Error", "No se pudo guardar el corte.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Vuelve a pedir un solo activo y lo sustituye en su lugar de la lista. */
  private refrescar(id: number): void {
    this.service.obtener(id).subscribe({
      next: (d) => {
        if (!d || !d.activo) {
          this.cargar();
          return;
        }
        const i = this.activos.findIndex((x) => x.idMatrizActivo === id);
        if (i >= 0) {
          this.activos[i] = d.activo;
          this.construirFilas();
        }
        this.cdr.detectChanges();
      },
      // Si el refresco puntual falla se recarga todo: más caro, pero seguro.
      error: () => this.cargar(),
    });
  }

  /* ---------- Una sola edicion abierta a la vez ---------- */

  /**
   * El activo que hoy tiene algo abierto: la tarjeta en edicion o uno de sus
   * cortes. Con dos tarjetas abiertas es facil guardar la equivocada, y aqui
   * pesa mas que en otras pantallas porque al guardar se manda el arbol entero.
   */
  private activoEditandose(): number | null {
    const enTarjeta = Object.keys(this.borradores);
    if (enTarjeta.length) {
      return Number(enTarjeta[0]);
    }
    const enCorte = Object.keys(this.corteEditando);
    if (enCorte.length) {
      const c = this.corteEditando[Number(enCorte[0])];
      return c && c.idMatrizActivo != null ? c.idMatrizActivo : null;
    }
    return null;
  }

  /** true cuando OTRA tarjeta tiene una edicion abierta. */
  bloqueado(a: MatrizActivo): boolean {
    const abierto = this.activoEditandose();
    return abierto !== null && abierto !== a.idMatrizActivo;
  }

  /** Hay alguna captura abierta, sea de quien sea. */
  get hayEdicionAbierta(): boolean {
    return this.activoEditandose() !== null;
  }

  avisoBloqueo(): string {
    const id = this.activoEditandose();
    if (id === null) {
      return "";
    }
    const a = this.activos.find((x) => x.idMatrizActivo === id);
    const folio = a && a.consecutivo ? a.consecutivo : "el activo abierto";
    return "Termina primero " + folio + ": guárdalo o cancélalo.";
  }

  /* ---------- Tarjetas contraídas ---------- */

  /**
   * Las tarjetas nacen contraidas: con la valoracion, el arbol y los cortes
   * desplegados la lista obliga a mucho scroll. Se guarda lo ABIERTO, que es
   * lo excepcional.
   */
  expandidos = new Set<number>();

  /** En edicion siempre abierta: no tendria sentido capturar a ciegas. */
  expandido(a: MatrizActivo): boolean {
    return this.expandidos.has(a.idMatrizActivo!) || this.enEdicion(a);
  }

  alternarTarjeta(a: MatrizActivo): void {
    const id = a.idMatrizActivo!;
    if (this.expandidos.has(id)) {
      this.expandidos.delete(id);
    } else {
      this.expandidos.add(id);
      // La bandera "aplica / no aplica" de evidencias se necesita en cuanto
      // el detalle es visible, para pintar la sección o su línea de No aplica.
      if (this.evidenciaAplica[id] === undefined) {
        this.cargarEvidencias(id);
      }
    }
  }

  /* ---------- Llegada desde el resumen de General ---------- */

  /** Tarjeta a la que se acaba de saltar; se resalta un momento y se apaga. */
  enfocado: number | null = null;

  /**
   * La llama el libro al dar clic en un renglon del resumen. Limpia filtro y
   * busqueda: con cualquiera de los dos puesto la tarjeta podria no estar en
   * pantalla y el scroll no encontraria nada.
   */
  enfocar(id?: number): void {
    if (!id) {
      return;
    }
    this.filtro = "Todas";
    this.busqueda = "";
    this.expandidos.add(id);
    this.enfocado = id;
    this.cdr.detectChanges();
    const el = document.getElementById("act-" + id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => {
      this.enfocado = null;
      this.cdr.detectChanges();
    }, 2400);
  }

  /* ---------- Bitácora ---------- */

  alternarBitacora(a: MatrizActivo): void {
    const id = a.idMatrizActivo!;
    if (this.bitacoraAbierta.has(id)) {
      this.bitacoraAbierta.delete(id);
      return;
    }
    this.bitacoraAbierta.add(id);
    if (this.bitacora[id]) {
      return;
    }
    this.cargaBitacora(id);
  }

  private cargaBitacora(id: number): void {
    this.cargandoBitacora.add(id);
    this.service.historial(id).subscribe({
      next: (res) => {
        this.cargandoBitacora.delete(id);
        this.bitacora[id] = (res && res.historial) || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoBitacora.delete(id);
        this.bitacora[id] = [];
        swal.fire("Error", "No se pudo consultar la bitácora.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Lo que pide la base (matriz-comun/hoja-matriz-base.ts) ---------- */

  /** El activo cuya comparación está abierta, o null si la ventana está cerrada. */
  get comparaActivo(): MatrizActivo | null {
    return this.comparaRegistro;
  }

  protected idDe(a: MatrizActivo): number | undefined {
    return a.idMatrizActivo;
  }

  /** El editor anidado (alta o edición) también se dibuja a pantalla completa. */
  protected formularioAbierto(): boolean {
    return this.idEditando !== null;
  }

  /**
   * Después de regresar a una copia: la bitácora, si está a la vista, y sólo
   * este activo, no la matriz entera.
   */
  protected trasRestaurarCopia(id: number): void {
    if (this.bitacoraAbierta.has(id)) {
      this.cargaBitacora(id);
    }
    this.refrescar(id);
  }

  /**
   * Las listas que dependen de lo que acaba de pasar en el servidor. Cada
   * guardado, cada corte y cada baja dejan una copia nueva; si no se releen,
   * la sección sigue enseñando la lista vieja y la copia recién tomada no
   * aparece hasta recargar la página.
   */
  private refrescarListas(id: number): void {
    delete this.respaldos[id];
    delete this.bitacora[id];
    if (this.respaldosAbiertos.has(id)) {
      this.cargaRespaldos(id);
    }
    if (this.bitacoraAbierta.has(id)) {
      this.cargaBitacora(id);
    }
  }

  /** El nombre legible del campo que cambió. */
  campo(h: MatrizHistorial): string {
    if (!h.campoModificado) {
      return "";
    }
    return MatrizCompletaIAComponent.ETIQUETAS[h.campoModificado] || h.campoModificado;
  }

  /**
   * Solo se llama cuando el movimiento no trae usuario. Se decide por el id y
   * no por el campo, igual que en el plan de gestión: el 0 es la marca que deja
   * una carga por script, y es lo único que no resuelve nombre. De ahí en
   * adelante el servicio graba siempre el id del usuario de la sesión.
   */
  quien(h: MatrizHistorial): string {
    return h.idUsuario === 0
      ? "Carga inicial desde el Excel"
      : "Sin usuario registrado";
  }

  claseAccion(accion?: string): string {
    switch (accion) {
      case "ALTA": return "a-alta";
      case "BAJA": return "a-baja";
      case "REACTIVACION": return "a-react";
      default: return "a-mod";
    }
  }

  /* ---------- Edición en la tarjeta ---------- */

  /**
   * Los datos propios del activo se corrigen aquí mismo, como en Plan Gestión
   * RSK. El árbol de amenazas no: son tres niveles anidados y para eso está el
   * editor completo.
   */
  borradores: { [id: number]: MatrizActivo } = {};
  guardandoActivo = new Set<number>();

  enEdicion(a: MatrizActivo): boolean {
    return !!(a.idMatrizActivo && this.borradores[a.idMatrizActivo]);
  }

  borrador(a: MatrizActivo): MatrizActivo {
    return this.borradores[a.idMatrizActivo!];
  }

  editarDatos(a: MatrizActivo): void {
    if (this.bloqueado(a)) {
      return;
    }
    this.expandidos.add(a.idMatrizActivo!);
    // Solo los campos capturables: el árbol y los cortes se quedan donde están.
    this.borradores[a.idMatrizActivo!] = {
      nombreActivo: a.nombreActivo,
      tipoActivo: a.tipoActivo,
      valConfidencialidad: a.valConfidencialidad,
      valIntegridad: a.valIntegridad,
      valDisponibilidad: a.valDisponibilidad,
      justificacionValoracion: a.justificacionValoracion,
      activosDependientes: a.activosDependientes,
      fechaIdentificacion: this.aFecha(a.fechaIdentificacion),
      idInventarioIa: a.idInventarioIa,
    };
  }

  cancelarEdicionDatos(a: MatrizActivo): void {
    delete this.borradores[a.idMatrizActivo!];
  }

  guardarDatos(a: MatrizActivo): void {
    const id = a.idMatrizActivo!;
    const b = this.borradores[id];
    if (!b || !b.nombreActivo || !b.nombreActivo.trim()) {
      swal.fire("Falta el nombre", "El nombre del activo es obligatorio.", "warning");
      return;
    }
    this.guardandoActivo.add(id);
    this.cdr.detectChanges();

    /*
     * Se relee el activo justo antes de guardar, y el árbol que se manda es el
     * recién traído, no el que está en pantalla.
     *
     * Importa: el backend reemplaza el árbol completo con lo que reciba. Si se
     * mandara el que se cargó al abrir la pantalla, y mientras tanto alguien
     * agregó una amenaza desde el editor, esa amenaza se borraría en silencio
     * al corregir aquí una falta de ortografía.
     */
    this.service.obtener(id).subscribe({
      next: (fresco) => {
        const vivo = fresco && fresco.activo;
        if (!vivo || !vivo.amenazas) {
          this.guardandoActivo.delete(id);
          swal.fire(
            "No se puede guardar",
            "No se pudo releer este activo con sus amenazas. Recarga la página e inténtalo de nuevo.",
            "warning"
          );
          this.cdr.detectChanges();
          return;
        }
        const payload: MatrizActivo = {
          ...vivo,
          nombreActivo: b.nombreActivo,
          tipoActivo: b.tipoActivo,
          valConfidencialidad: b.valConfidencialidad,
          valIntegridad: b.valIntegridad,
          valDisponibilidad: b.valDisponibilidad,
          justificacionValoracion: b.justificacionValoracion,
          activosDependientes: b.activosDependientes,
          fechaIdentificacion: b.fechaIdentificacion,
          idInventarioIa: b.idInventarioIa,
        };
        this.service.actualizar(payload).subscribe({
          next: (d) => {
            this.guardandoActivo.delete(id);
            if (!d || d.codigoError) {
              swal.fire("No se guardó", (d && d.mensajeError) || "Error desconocido.", "warning");
              this.cdr.detectChanges();
              return;
            }
            delete this.borradores[id];
            if (quedoComoPropuesta(d)) {
              void avisarPropuesta("El cambio de " + (a.consecutivo || "este activo"));
            }
            // La bitácora cambió y el servidor acaba de guardar una copia:
            // las dos listas se descartan y, si están a la vista, se releen.
            this.refrescarListas(id);
            this.refrescar(id);
          },
          error: () => {
            this.guardandoActivo.delete(id);
            swal.fire("Error", "No se pudo guardar el activo.", "error");
            this.cdr.detectChanges();
          },
        });
      },
      error: () => {
        this.guardandoActivo.delete(id);
        swal.fire("Error", "No se pudo releer el activo antes de guardar.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Alta, edición y baja ---------- */

  /** 0 abre el editor en alta; un id lo abre en edición. */
  editar(a: MatrizActivo): void {
    if (this.bloqueado(a)) {
      return;
    }
    this.idEditando = a.idMatrizActivo || null;
    this.sincronizarPantalla();
  }

  agregarActivo(): void {
    this.idEditando = 0;
    this.sincronizarPantalla();
  }

  cerrarEditor(guardado: boolean): void {
    this.idEditando = null;
    this.sincronizarPantalla();
    if (guardado) {
      this.cargar();
    }
  }

  eliminar(a: MatrizActivo): void {
    if (this.bloqueado(a)) {
      return;
    }
    const c = this.cuenta(a);
    swal
      .fire({
        title: "¿Eliminar " + (a.consecutivo || "este activo") + "?",
        text:
          "Se borra el activo con sus " + c.amenazas + " amenaza(s), " +
          c.vulnerabilidades + " vulnerabilidad(es), " + c.acciones + " acción(es) y " +
          (a.seguimientos ? a.seguimientos.length : 0) + " corte(s) de monitoreo. " +
          "Se guarda una copia antes de borrarlo: si hace falta, vuelve con su folio " +
          "desde el botón «Eliminados».",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#b3261e",
      })
      .then((r) => {
        if (!r.isConfirmed || !a.idMatrizActivo) {
          return;
        }
        this.service.eliminar(a.idMatrizActivo).subscribe({
          next: (res) => {
            if (!res || res.codigoError) {
              swal.fire("No se eliminó", (res && res.mensajeError) || "Error desconocido.", "warning");
              return;
            }
            // Propuesta: el activo sigue en la matriz hasta que se acepte.
            if (quedoComoPropuesta(res)) {
              void avisarPropuesta("La eliminación de " + (a.consecutivo || "este activo"));
            }
            this.cargar();
            // Lo que se acaba de borrar tiene que aparecer en la papelera si
            // está abierta: si no, parece que no se guardó copia.
            if (this.papeleraAbierta) {
              this.cargaPapelera();
            }
          },
          error: () => swal.fire("Error", "No se pudo eliminar el activo.", "error"),
        });
      });
  }

  /** Baja lógica: el activo deja de contar sin perder su historia. */
  alternarEstatus(a: MatrizActivo): void {
    if (this.bloqueado(a)) {
      return;
    }
    const baja = a.estatus === "ACTIVO";
    swal
      .fire({
        title: baja ? "¿Dar de baja " + a.consecutivo + "?" : "¿Reactivar " + a.consecutivo + "?",
        text: baja
          ? "Queda registrado como INACTIVO. No se borra nada y se puede reactivar."
          : "Vuelve a contar como activo vigente de la matriz.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: baja ? "Dar de baja" : "Reactivar",
        cancelButtonText: "Cancelar",
      })
      .then((r) => {
        if (!r.isConfirmed || !a.idMatrizActivo) {
          return;
        }
        this.service
          .cambiarEstatus(a.idMatrizActivo, baja ? "INACTIVO" : "ACTIVO")
          .subscribe({
            next: (d) => {
              // Cuerpo vacío o con código: el backend responde 200 aunque
              // falle, así que sin esto la baja se daba por hecha sin serlo.
              if (!d || d.codigoError) {
                swal.fire("No se aplicó", (d && d.mensajeError) || "Error desconocido.", "warning");
                return;
              }
              if (quedoComoPropuesta(d)) {
                void avisarPropuesta((baja ? "La baja de " : "La reactivación de ") + (a.consecutivo || "este activo"));
              }
              this.cargar();
            },
            error: () => swal.fire("Error", "No se pudo cambiar el estatus.", "error"),
          });
      });
  }
}
